import {
  ActionIcon,
  Box,
  Group,
  Stack,
  Text,
  Badge,
  Collapse,
  UnstyledButton,
} from '@mantine/core';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  MessageCircle,
} from 'react-feather';
import { useState, type ReactNode } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import type {
  AuthoredAgendaSectionForCoach,
  AiPromptSegmentSelections,
  AiPromptSelectionsDoc,
  CoachPrepGroupContext,
  CoachSessionLeaderNote,
  CoachLeaderNotesScope,
} from '../../../../services/api/coachSessionPrep';
import type {
  ComposeEnvelope,
  PrepSuggestionsOutput,
} from '../../../../services/api/aiTypes';
import { LeaderNotesPanel } from './LeaderNotesPanel';
import { AIPromptSuggestionsSection } from './AIPromptSuggestionsSection';
import { RichTextContent } from '../../../../components/common/RichTextEditor';
import { SectionSlideThumbnails } from '../../../../components/common/slides/SectionSlideThumbnails';
import { ContentCard } from '../../../../components/common/ContentCard';
import {
  SectionContentBox,
  SectionNumber,
  ACCORDION_BORDER,
  ROW_EXPANDED_BG,
  STEP_CONTENT_BG,
  STEP_LABEL_COLOR,
  PARTICIPANT_BG,
  PARTICIPANT_ACCENT,
} from './agendaSectionPrimitives';

/**
 * MED-976 (E1.3h) — Authored Session Content Accordion.
 *
 * Sibling component to the original `SessionAgendaAccordion` (which renders
 * the parsed-PDF "section → children blocks" shape). This one renders the
 * authored "sections" model from e1.3d/e/f:
 *   - section name + duration + slide-page associations
 *   - per-section Step Content (leader prompts: Intro Text + Discussion Prompt)
 *   - per-section Participant Prompt read-only ("What participants see")
 *
 * Coach mode extras (Leader Notes panel + AI Suggestions section per step) are
 * keyed by the STABLE section id (`section.id`), matching the peer-leader
 * accordion. Leader Notes were re-keyed in MED-1120 (e1.3p) and coach AI "In
 * Use" selections in MED-1122 (e1.3q); both now survive CM content edits, follow
 * a reorder, and clear only on section removal. (Legacy parsed-outline rows
 * written under positional `"<sectionIdx>:<blockIdx>"` keys no longer resolve
 * here — UAT-only data behind the flag, fix-forward, per both tickets.)
 *
 * MED-1093 — Session Prep polish (shared coach + peer-leader): the agenda is a
 * collapsible `ContentCard` (whole-agenda chevron coexisting with the prep
 * action buttons), the "Leader Prompts" block is renamed "Step Content" with
 * the slide thumbnails pulled inside it, and the prep-sheet AI banner is
 * dropped. Leader Notes editor (MED-1094) and AI Suggestions (MED-1098) are
 * their own stories and unchanged here.
 *
 * The original `SessionAgendaAccordion` is preserved verbatim — it's still
 * used by the CM editor preview and by the legacy `parsed_outline` fallback
 * inside CoachSessionPrepPage when the live_session_delivery flag is OFF.
 */

// MED-1093 — agenda header tokens, matched to the Figma (node 1991:3680).
const AGENDA_TITLE_COLOR = '#1A1A2E';
const AGENDA_ICON_COLOR = '#6B7280';
const AGENDA_META_COLOR = '#666666';
const AGENDA_PILL_BG = '#F3F4F6';
const AGENDA_PILL_TEXT = '#888888';

export interface AuthoredSessionAgendaCoachContext {
  groups: CoachPrepGroupContext[];
  leaderNotes: CoachSessionLeaderNote[];
  onSaveLeaderNote: (args: {
    blockKey: string;
    scope: CoachLeaderNotesScope;
    groupId: string | null;
    contentHtml: string;
  }) => Promise<void>;
  headerActionsSlot?: ReactNode;
  startCollapsed?: boolean;
  sessionId: string;
  aiFeaturesEnabled: boolean;
  aiPromptSelections: AiPromptSelectionsDoc;
  onSaveAiSelections: (
    segmentKey: string,
    next: AiPromptSegmentSelections
  ) => Promise<void>;
  /**
   * MED-1098 — DEV preview only: pre-seeded AI suggestions envelope handed to
   * every per-step AI Suggestions section so the prompt cards + AI note render
   * without the AI backend. Undefined in production (sections fetch live).
   */
  previewAiEnvelope?: ComposeEnvelope<PrepSuggestionsOutput>;
}

export interface AuthoredSessionAgendaAccordionProps {
  sections: AuthoredAgendaSectionForCoach[];
  /** Coach context drives leader notes + AI surfaces. Required for the coach prep page. */
  coachContext: AuthoredSessionAgendaCoachContext;
  /**
   * MED-1079 — the loaded slide-deck PDF. When present, a section's associated
   * slides render as thumbnails (matching the content editor); while loading or
   * absent the renderer falls back to `📄 Page N` chips. The page loads the deck
   * once via `useSlideDeckDoc` and passes it down here.
   */
  slideDeckDoc?: PDFDocumentProxy | null;
}

export function AuthoredSessionAgendaAccordion({
  sections,
  coachContext,
  slideDeckDoc = null,
}: AuthoredSessionAgendaAccordionProps) {
  const totalDuration = sections.reduce(
    (sum, s) => sum + (s.duration_minutes ?? 0),
    0
  );
  const totalSections = sections.length;
  const sectionsWithNotes = new Set(
    coachContext.leaderNotes.map(n => n.block_key)
  ).size;

  // MED-1093 AC2 — the whole agenda collapses/expands via the header chevron.
  // Loads collapsed when the session has been marked as reviewed (same
  // `startCollapsed` signal the step rows use), expanded otherwise.
  const [collapsed, setCollapsed] = useState(
    Boolean(coachContext.startCollapsed)
  );

  return (
    <ContentCard
      data-testid="coach-prep-agenda"
      variant="elevated"
      radius={16}
      padding="lg"
      contentGap="md"
      fill={false}
      header={
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group align="center" gap={24} wrap="nowrap" style={{ minWidth: 0 }}>
            <Group align="center" gap={12} wrap="nowrap">
              <Calendar
                size={20}
                color={AGENDA_ICON_COLOR}
                aria-hidden="true"
                style={{ marginTop: -4 }}
              />
              <Text fz={17} fw={600} c={AGENDA_TITLE_COLOR}>
                Session Agenda
              </Text>
              <Badge
                radius="xl"
                tt="uppercase"
                styles={{
                  root: {
                    backgroundColor: AGENDA_PILL_BG,
                    color: AGENDA_PILL_TEXT,
                    letterSpacing: '0.6px',
                  },
                  label: {
                    fontSize: 12,
                    fontWeight: 600,
                    transform: 'translateY(1px)',
                  },
                }}
              >
                All Groups
              </Badge>
            </Group>
            <Text fz={13} c={AGENDA_META_COLOR}>
              Total: {totalDuration} minutes · {sectionsWithNotes} of{' '}
              {totalSections} steps have leader notes
            </Text>
          </Group>
          {/* MED-1093 AC3 — prep action buttons stay in the header and coexist
              with the collapse chevron; the chevron is a separate click target
              and both remain visible when the agenda is collapsed. */}
          <Group gap="xs" wrap="nowrap" align="center">
            {coachContext.headerActionsSlot}
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setCollapsed(c => !c)}
              aria-label={collapsed ? 'Expand agenda' : 'Collapse agenda'}
              data-testid="coach-prep-agenda-toggle"
            >
              {collapsed ? (
                <ChevronRight size={18} />
              ) : (
                <ChevronDown size={18} />
              )}
            </ActionIcon>
          </Group>
        </Group>
      }
    >
      <Collapse in={!collapsed}>
        {/* MED-1093 — the accordion lives in a single outlined container nested
            in the card (Figma): 1px border, radius 12, rows divided by 1px
            bottom borders (expanded rows fill grey). overflow:hidden clips the
            row fills/dividers to the rounded corners. */}
        <Box
          style={{
            border: `1px solid ${ACCORDION_BORDER}`,
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          {sections.map((section, sectionIdx) => (
            <AuthoredSectionRow
              key={section.id}
              section={section}
              sectionIdx={sectionIdx}
              isLast={sectionIdx === sections.length - 1}
              coachContext={coachContext}
              slideDeckDoc={slideDeckDoc}
            />
          ))}
        </Box>
      </Collapse>
    </ContentCard>
  );
}

interface AuthoredSectionRowProps {
  section: AuthoredAgendaSectionForCoach;
  sectionIdx: number;
  isLast: boolean;
  coachContext: AuthoredSessionAgendaCoachContext;
  slideDeckDoc: PDFDocumentProxy | null;
}

function AuthoredSectionRow({
  section,
  sectionIdx,
  isLast,
  coachContext,
  slideDeckDoc,
}: AuthoredSectionRowProps) {
  // AC4 parity with the original accordion: coach mode starts expanded UNTIL
  // marked-as-reviewed flips startCollapsed.
  const initialOpen = !coachContext.startCollapsed;
  const [open, setOpen] = useState(initialOpen);

  // MED-1120 (E1.3p) — Leader Notes are keyed by the STABLE section id so they
  // survive CM content edits (wording/reorder/insert) and only clear when the
  // CM removes the section. Section ids are now identity-stable on save
  // (batchUpsertAgenda reconciles by id), so this key holds across edits.
  const notesKey = section.id;
  // MED-1122 (E1.3q) — Coach AI "In Use" selections are now keyed by the STABLE
  // section id (was positional `"${sectionIdx}:0"`), matching peer-leader
  // (PeerLeaderAgendaSectionList) and Leader Notes above. With e1.3p's stable
  // ids, selections survive CM wording edits, follow a reorder, and clear only
  // when the section is removed (the latter enforced in the in-session read's
  // flatten-filter — CoachActiveSessionService.flattenAiPromptSelections). The
  // positional `segmentIndex` below still drives live AI generation by slot.
  const sectionTitle = section.section_name ?? 'Untitled section';

  const sortedPrompts = section.leader_prompts
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  const sortedVisuals = section.visuals
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  const hasStepContent = sortedPrompts.length > 0 || sortedVisuals.length > 0;

  return (
    <div
      style={{
        borderBottom: isLast ? undefined : `1px solid ${ACCORDION_BORDER}`,
        background: open ? ROW_EXPANDED_BG : undefined,
      }}
    >
      <UnstyledButton
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'block',
          width: '100%',
          padding: 'var(--mantine-spacing-md) var(--mantine-spacing-lg)',
        }}
      >
        <Group justify="space-between" align="center">
          <Group align="center" gap="md">
            <SectionNumber n={sectionIdx + 1} active={open} />
            <Text fw={600}>{sectionTitle}</Text>
          </Group>
          <Group gap="sm" align="center">
            {section.duration_minutes !== null && (
              <Group gap={4} align="center">
                <Clock size={14} />
                <Text size="sm" c="dimmed">
                  {section.duration_minutes} min
                </Text>
              </Group>
            )}
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </Group>
        </Group>
      </UnstyledButton>

      <Collapse in={open}>
        <Stack
          gap="md"
          style={{
            // MED-1093 — content spans the full row width (aligned to the row's
            // left padding), not indented under the title past the number.
            padding: '0 var(--mantine-spacing-lg) var(--mantine-spacing-md)',
          }}
        >
          {/* MED-1093 AC5 — "Step Content" box: the section's leader prompt(s)
              with the associated slide thumbnails pulled in below the text
              (no separate "Slides" block). The thumbnails render once the deck
              PDF is loaded; otherwise `Page N` chips stand in. */}
          {hasStepContent && (
            <SectionContentBox
              icon={
                <FileText
                  size={12}
                  color={STEP_LABEL_COLOR}
                  aria-hidden="true"
                />
              }
              label="Step Content"
              labelColor={STEP_LABEL_COLOR}
              background={STEP_CONTENT_BG}
            >
              {sortedPrompts.length > 0 && (
                <Stack gap="sm">
                  {sortedPrompts.map((prompt, promptIdx) => {
                    const hasIntro =
                      !!prompt.intro_text &&
                      prompt.intro_text.trim().length > 0;
                    const hasBody =
                      !!prompt.discussion_prompt &&
                      prompt.discussion_prompt.trim().length > 0;
                    // MED-1093 AC6 — a prompt with a label but no body shows just
                    // the label; a fully-empty prompt renders nothing (no literal
                    // "(no prompt body)" placeholder).
                    if (!hasIntro && !hasBody) return null;
                    return (
                      <div key={promptIdx}>
                        {hasIntro && (
                          <Text size="xs" c="dimmed" fw={600} mb={4}>
                            {prompt.intro_text!.trim()}
                          </Text>
                        )}
                        {hasBody && (
                          <RichTextContent
                            content={prompt.discussion_prompt}
                            emptyText=""
                          />
                        )}
                      </div>
                    );
                  })}
                </Stack>
              )}

              {sortedVisuals.length > 0 && (
                <Box mt={sortedPrompts.length > 0 ? 'sm' : 0}>
                  {slideDeckDoc ? (
                    <SectionSlideThumbnails
                      doc={slideDeckDoc}
                      visuals={sortedVisuals}
                    />
                  ) : (
                    <Group gap="xs" align="center">
                      {sortedVisuals.map(v => (
                        <Badge
                          key={v.slide_page}
                          variant="light"
                          color="gray"
                          radius="xl"
                          leftSection={<FileText size={10} />}
                          styles={{
                            label: {
                              fontSize: 12,
                              fontWeight: 600,
                              transform: 'translateY(1px)',
                            },
                          }}
                        >
                          Page {v.slide_page}
                        </Badge>
                      ))}
                    </Group>
                  )}
                </Box>
              )}
            </SectionContentBox>
          )}

          {/* AC-CP4 — Participant Prompt read-only, labeled so the facilitator knows what
              participants will see. Render via RichTextContent for WYSIWYG fidelity.
              MED-1093: kept as-is (UX-expert to define its final treatment). */}
          {section.participant_prompt &&
            section.participant_prompt.trim().length > 0 && (
              <SectionContentBox
                icon={
                  <MessageCircle
                    size={12}
                    color={PARTICIPANT_ACCENT}
                    aria-hidden="true"
                  />
                }
                label="What participants see"
                labelColor={PARTICIPANT_ACCENT}
                background={PARTICIPANT_BG}
              >
                <RichTextContent
                  content={section.participant_prompt}
                  emptyText=""
                />
              </SectionContentBox>
            )}

          {/* MED-1120 (E1.3p) — Leader Notes panel, keyed by the stable section id. */}
          <LeaderNotesPanel
            blockKey={notesKey}
            groups={coachContext.groups}
            notes={coachContext.leaderNotes.filter(
              n => n.block_key === notesKey
            )}
            onSave={coachContext.onSaveLeaderNote}
          />

          {/* MED-876 AC2 — AI Suggestions section per authored section. MED-1122
              (E1.3q): "In Use" selections key by the stable section id (write,
              read, and save), matching peer-leader. segmentIndex stays positional
              — it drives the live AI generation fetch, not selection storage. */}
          {coachContext.aiFeaturesEnabled && (
            <AIPromptSuggestionsSection
              sessionId={coachContext.sessionId}
              groupId={coachContext.groups[0]?.group_id}
              segmentIndex={sectionIdx}
              segmentKey={section.id}
              currentSelections={coachContext.aiPromptSelections[section.id]}
              onSelectionsChange={next =>
                coachContext.onSaveAiSelections(section.id, next)
              }
            />
          )}
        </Stack>
      </Collapse>
    </div>
  );
}
